import { Box, Flex, Tag } from '@chakra-ui/react';
import React from 'react';

import type { FieldItem } from '../../config';
import { useCodingScreen } from '../../interface';
import { readState } from '../../slots/readState';
import { renderPlainText } from '../../slots/renderPlainText';
import {
  BAR_BG,
  BAR_BORDER,
  PATIENT_BAR_H,
  SHELL_PAD
} from '../../styles/tokens';
import { Slot } from '../Slot';

/** Ascending by `order`, dropping any field marked `visible:false` -- the ordering/visibility
 *  contract every `FieldItem`-driven container in this folder follows. `.filter` on a `readonly
 *  FieldItem[]` already returns a fresh mutable array, so `.sort` never mutates the config's own
 *  (readonly, `as const`) field list. */
function visibleFieldsInOrder(fields: readonly FieldItem[]): FieldItem[] {
  return fields
    .filter((field) => field.visible)
    .sort((a, b) => a.order - b.order);
}

/**
 * Middle header bar -- patient/encounter summary + case actions (full population is a later
 * story). By default `header.patientSummary.fields` is `[]` so this renders an empty, labelled
 * `role="region"` container; when a config supplies fields, each renders as a reused Chakra `Tag`,
 * proving the atom-reuse requirement.
 *
 * `header.patientSummary.visible` gates the CONTAINER itself, same geometry-preserving hide
 * contract as `TopBarContainer`: `visible:false` sets `visibility:hidden` + `aria-hidden`, never
 * `display:none`, so fixed header geometry never collapses; `data-content-key` stays present in
 * both states so tests/future wiring can always find the container.
 *
 * Each field renders in host chrome selected by `field.presentation` (CDMX-18412): the default
 * (`'tag'`/absent) wraps the slot in the reused Chakra `Tag` (`data-testid="field-tag"`); `'plain'`
 * renders a bare positioning `Box` (`data-testid="field-slot"`) with no Tag padding/bg/radius, so an
 * action slot (e.g. HoldAction's icon button) isn't boxed in a pill. The field's body is a `Slot`
 * reading `field.contentId`'s bound value out of injected `state` -- when `field.componentId`
 * resolves in the registry the component renders in place of plain text, otherwise the fallback
 * (`renderPlainText(value) ?? field.label`) reproduces the original behavior: bound value as text,
 * or `field.label` when there's no bound value.
 */
export const PatientSummaryBar: React.FC = () => {
  const { config, state } = useCodingScreen();
  const { mode } = config;
  const { contentKey, visible, fields } = config.header.patientSummary;
  const ordered = visibleFieldsInOrder(fields);
  // Action slots (`presentation:'plain'`, e.g. HoldAction) group to the RIGHT edge of the bar: the
  // first action field takes `marginLeft:auto`, consuming the free space after the left-aligned
  // info fields (PatientInfo, etc.), which pushes it and every trailing action to the right.
  const firstActionKey = ordered.find(
    (f) => f.presentation === 'plain'
  )?.contentKey;

  return (
    <Flex
      role="region"
      aria-label="Patient summary"
      data-content-key={contentKey}
      aria-hidden={visible ? undefined : true}
      align="center"
      gap={2}
      px={SHELL_PAD}
      bg={BAR_BG}
      borderBottom={`1px solid ${BAR_BORDER}`}
      style={{
        height: PATIENT_BAR_H,
        minHeight: PATIENT_BAR_H,
        visibility: visible ? 'visible' : 'hidden'
      }}
    >
      {ordered.map((field) => {
        const value = readState(state, field.contentId);
        const slot = (
          <Slot
            item={field}
            value={value}
            mode={mode}
            fallback={() => renderPlainText(value) ?? field.label}
          />
        );
        return field.presentation === 'plain' ? (
          <Box
            key={field.contentKey}
            data-slot-key={field.contentKey}
            data-testid="field-slot"
            display="flex"
            alignItems="center"
            marginLeft={field.contentKey === firstActionKey ? 'auto' : undefined}
          >
            {slot}
          </Box>
        ) : (
          <Tag
            key={field.contentKey}
            data-slot-key={field.contentKey}
            data-testid="field-tag"
          >
            {slot}
          </Tag>
        );
      })}
    </Flex>
  );
};
